import {injectable} from 'inversify';
import {makeAutoObservable} from 'mobx';

import type {DateTimeModel} from '../../../../core/domain/Date/DateTime.model';
import type {Destructible} from '../../../../utils/store';
import type {DataFilterState} from '../../models/DataFilterState';
import type {DataFilterStore} from '../DataFilter/DataFilter.store';

import {FlagManager} from '../../../../utils/store';

@injectable()
export class DataFilterDatesManagerStore<Data = any> implements Destructible {
  isInputOpen = new FlagManager();
  isReady = new FlagManager();
  private dateKeys: DataFilterStore.DatesConfig<Data> = {
    endKey: null,
    startKey: null,
  };
  private store: DataFilterStore<Data> | null = null;

  constructor() {
    makeAutoObservable(this, undefined, {
      autoBind: true,
    });
  }

  init(
    config: DataFilterStore.DatesConfig<Data>,
    store: DataFilterStore<Data>,
  ) {
    this.store = store;
    this.dateKeys = {...config};
    this.isReady.setTrue();
  }

  destruct() {}

  get endDate() {
    return this.value.end?.toDate() ?? null;
  }

  get isLoading() {
    return Boolean(this.store?.isLoading);
  }

  get startDate() {
    return this.value.start?.toDate() ?? null;
  }

  get value() {
    return this.store?.state.datesFilter || {end: null, start: null};
  }

  clear() {
    this.store?.setState(this.store.state.updateDates(null));
  }

  filterData(data: Data[], state: DataFilterStore.State): Data[] {
    const isNoKeysToFilter = !this.dateKeys.startKey || !this.dateKeys.endKey;

    if (!state.datesFilter || isNoKeysToFilter) {
      return data;
    }

    const {end, start} = state.datesFilter;

    return data.filter(item => {
      const startDate = item[this.dateKeys.startKey!] as DateTimeModel;
      const endDate = item[this.dateKeys.endKey!] as DateTimeModel;

      const isAfterStart = !start || startDate.gte(start);
      const isBeforeEnd = !end || endDate.lte(end);

      return isAfterStart && isBeforeEnd;
    });
  }

  onChange(value: DataFilterState.DatesFilter) {
    const shouldClear = !value || (!value.start && !value.end);

    if (shouldClear) {
      return this.clear();
    }

    if (this.store) {
      const nextState = this.store.state.updateDates(value);

      this.store.setState(nextState);
    }
  }
}
